import React from 'react';
import { Box } from '@mui/material';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import { useTranslation } from 'react-i18next';

import type { IAssistantScript } from '@shared/interfaces/assistantScript';

import { useAiInspectorStyles } from './inspectorStyles';

const POLICY_LABEL_KEY: Record<
  IAssistantScript['playbackPolicy'] & string,
  { key: string; fallback: string }
> = {
  always: { key: 'editorMode.aiPolicyAlways', fallback: 'Always' },
  once_session: {
    key: 'editorMode.aiPolicyOnceSession',
    fallback: 'Once per session',
  },
  once_user: { key: 'editorMode.aiPolicyOnceUser', fallback: 'Once per user' },
  first_time: {
    key: 'editorMode.aiPolicyFirstTime',
    fallback: 'First time only',
  },
};

interface IMetaChipsProps {
  script: IAssistantScript;
}

const MetaChips = ({ script }: IMetaChipsProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();

  const policy = script.playbackPolicy;
  const policyLabel = policy
    ? POLICY_LABEL_KEY[policy as keyof typeof POLICY_LABEL_KEY]
    : null;
  const voiceLabel = [script.voiceId, script.engine]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' · ');

  return (
    <Box sx={classes.metaChips}>
      {policyLabel ? (
        <Box component="span" sx={classes.metaChip}>
          <LoopOutlinedIcon />
          {translate(policyLabel.key, policyLabel.fallback)}
        </Box>
      ) : null}
      {voiceLabel ? (
        <Box component="span" sx={classes.metaChip}>
          <VolumeUpOutlinedIcon />
          {voiceLabel}
        </Box>
      ) : null}
      {script.triggerType === 'ui_click' && script.uiClickButtonLabel ? (
        <Box component="span" sx={classes.metaChip}>
          <TouchAppOutlinedIcon />
          {script.uiClickButtonLabel}
        </Box>
      ) : null}
    </Box>
  );
};

export default MetaChips;
